/**
 * RCD — Single-CD detail record.
 *
 * Stage ribbon: Issued → Active → Maturing → Matured/Rolled.
 *
 * Data source (updated 2026-04-22):
 *   Live jXchange AcctInq dual-call via `useCdDetail` → `CdInqService`.
 *   See `services/CdInqService.ts` for the port from legacy SPFx
 *   `CdMaintenanceScreen.tsx:144-196` (two parallel calls for
 *   `x_TimeDepInfoRec` + `x_TimeDepRateIntInfo`).
 *
 * No fixture values anywhere — if Core Director omits a field, UI shows "—".
 */
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft, FileText, PauseCircle, RefreshCw } from '@retail-ops/panel-shell/fi';
import {
  StageRibbon,
  Pill,
  WidgetCard,
  Button,
  KVPair,
  CollapsibleSection,
  Banner,
} from '@/components/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { useCdDetail } from './hooks/useCdDetail';
import type { CdDetail as CdDetailData } from './types';
import { formatMoney, formatShortDate } from './utils/cdFormat';
import './rcd.css';

type CdStage = 0 | 1 | 2 | 3;

const STAGE_LABELS: ReadonlyArray<string> = ['Issued', 'Active', 'Maturing', 'Matured / Rolled'];

function resolveStage(d: CdDetailData): CdStage {
  if (d.graceActive) return 2;
  if (d.daysToMaturity == null) return 1; // assume active when Core omits maturity
  if (d.daysToMaturity < 0) return 3;
  if (d.daysToMaturity <= 30) return 2;
  if (d.daysToMaturity <= 365) return 1;
  return 0;
}

/** Safe currency cell — renders "—" when null. */
function money(v: number | null): string {
  return v == null ? '—' : formatMoney(v);
}

/** Safe percentage cell. */
function pct(v: number | null): string {
  return v == null ? '—' : `${v.toFixed(2)}%`;
}

/** Safe term cell. */
function term(v: number | null): string {
  return v == null ? '—' : `${v} months`;
}

/** Safe ISO date cell. */
function iso(v: string | null): string {
  return v == null ? '—' : formatShortDate(v);
}

export function CdDetail(): JSX.Element {
  const { acctId } = useParams<{ acctId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, loading, error } = useCdDetail(acctId);
  // BACKLOG-1297 — the "Open as IRA" pointer used to be a window.alert(),
  // which Teams silently swallows; it now renders in the Actions card.
  const [notice, setNotice] = useState<string | null>(null);

  const stageIndex = useMemo<CdStage>(() => (data ? resolveStage(data) : 0), [data]);
  const stages = useMemo(() => STAGE_LABELS.map((label) => ({ label })), []);

  if (loading) {
    return (
      <div className="rcd-detail">
        <p className="rcd-loading">Loading CD {acctId}…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rcd-detail">
        <div className="rcd-banner rcd-banner--err" role="alert">
          {error}
        </div>
        <Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>
          Back to dashboard
        </Button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="rcd-detail">
        <div className="rcd-banner rcd-banner--err" role="alert">
          CD {acctId} not found.
        </div>
        <Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>
          Back to dashboard
        </Button>
      </div>
    );
  }

  // Wave 1 G-7 — every ROP-Teams user is an Officer-or-higher; Customer is
  // no longer a role. Surface a true gate against the panel-only roles
  // (Risk / SystemOps) which shouldn't see this action.
  const canRedeem = user?.primaryRole !== 'Risk' && user?.primaryRole !== 'SystemOps';

  return (
    <div className="rcd-detail">
      <div className="rcd-detail__stage-row">
        <div className="rcd-detail__crumb">
          <Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>
            Dashboard
          </Button>
          <h1>
            CD {data.acctId}
            <span className="rcd-detail__crumb-sub">{data.productDescription ?? data.productCode ?? ''}</span>
          </h1>
          {data.graceActive && (
            <Pill variant="warn">Grace · {data.graceDaysRemaining ?? 0}d left</Pill>
          )}
          {data.autoRenew === false && <Pill variant="muted">Manual renewal</Pill>}
          {data.autoRenew === null && <Pill variant="muted">Renewal —</Pill>}
        </div>
        <StageRibbon stages={[...stages]} currentIndex={stageIndex} />
      </div>

      {data.graceActive && (
        <div className="rcd-banner rcd-banner--warn" role="status">
          <div>
            <strong>Grace period active.</strong> CD continues to earn interest for{' '}
            {data.graceDaysRemaining ?? 0} more days. Officer action: none required — Core
            Director auto-renews unless customer requests redemption.
          </div>
        </div>
      )}

      <WidgetCard title="Summary" stripe="pipeline">
        <div className="rcd-detail__kvgrid">
          <KVPair label="CIF" value={data.cifId ?? '—'} />
          <KVPair label="Branch" value={data.branchCode ?? '—'} />
          <KVPair label="Balance" value={money(data.balance)} />
          <KVPair label="APR" value={pct(data.currentRate)} />
          <KVPair label="Term" value={term(data.termMonths)} />
          <KVPair label="Opened" value={iso(data.openedDate)} />
          <KVPair label="Matures" value={iso(data.maturityDate)} />
          <KVPair label="Status" value={data.statusDescription ?? '—'} />
        </div>
      </WidgetCard>

      <CollapsibleSection title="Interest & earnings">
        <div className="rcd-detail__kvgrid">
          <KVPair label="Accrued interest" value={money(data.accruedInterest)} />
          <KVPair label="Next interest date" value={iso(data.nextInterestDate)} />
          <KVPair label="Disposition" value={data.interestDisposition ?? '—'} />
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Penalty & renewal">
        <div className="rcd-detail__kvgrid">
          <KVPair label="Early-withdrawal penalty" value={money(data.penaltyAmount)} />
          <KVPair label="Penalty formula code" value={data.penaltyFormulaCode ?? '—'} />
          <KVPair label="Renewal code" value={data.renewalCode ?? '—'} />
          <KVPair label="Renewal count" value={data.renewalCount == null ? '—' : String(data.renewalCount)} />
          <KVPair label="Last renewed" value={iso(data.lastRenewalDate)} />
        </div>
      </CollapsibleSection>

      <WidgetCard title="Actions" stripe="approvals">
        <div className="rcd-detail__actions">
          <Button
            variant="primary"
            startIcon={<RefreshCw size={14} />}
            onClick={() => navigate('maturity')}
            disabled={!canRedeem}
          >
            Maturity instructions
          </Button>
          <Button
            variant="primary"
            startIcon={<PauseCircle size={14} />}
            onClick={() => navigate('waive')}
            disabled={!canRedeem}
          >
            Waive early-withdrawal penalty
          </Button>
          <Button
            variant="ghost"
            startIcon={<FileText size={14} />}
            onClick={() => navigate('lost')}
          >
            Lost-CD indemnity (rare)
          </Button>
          <Button
            variant="ghost"
            onClick={() =>
              setNotice(
                'IRA CDs are handled by RIM. Follow the link from the customer profile to open the IRA plan record.',
              )
            }
          >
            Open as IRA (RIM)
          </Button>
        </div>
        {notice && (
          <Banner tone="info">
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-4)' }}>
              <span style={{ flex: 1 }}>{notice}</span>
              <Button size="sm" variant="ghost" onClick={() => setNotice(null)}>Dismiss</Button>
            </div>
          </Banner>
        )}
      </WidgetCard>
    </div>
  );
}
